import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { isEqual, isPast } from 'date-fns';
import type { ComponentProps, CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ShieldButton } from '@sorare/core/src/atoms/buttons/ShieldButton';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, BrandXXS } from '@sorare/core/src/atoms/typography/index.tsx';
import { LiveIndicator } from '@sorare/core/src/atoms/ui/LiveIndicator/index.tsx';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';
import { range } from '@sorare/core/src/lib/arrays.ts';

import { LeagueDelayedEntryChip } from '#src/components/league/LeagueDelayedEntryChip/index.tsx';
import { PickerOption } from '#src/components/pickers/BoardPicker/PickerCard.tsx';
import { PickerHeader } from '#src/components/pickers/PickerHeader/index.tsx';
import { PickerOptionsGrid } from '#src/components/pickers/PickerOptionsGrid.tsx';
import { isFixtureLive } from '#src/lib/so5.ts';

import type { LeaderboardGroupPicker_so5LeaderboardGroup } from './__generated__/index.graphql.ts';

const GroupIcon = styled.img`
  width: var(--triple-unit);
  height: var(--triple-unit);
  object-fit: contain;

  &.md {
    width: var(--quadruple-unit);
    height: var(--quadruple-unit);
    margin-top: var(--intermediate-unit);
  }
`;

const LockOverlay = styled(FontAwesomeIcon)`
  position: absolute;
  top: var(--unit);
  left: var(--unit);
  color: var(--c-nd-400);
`;

const GridIcon = styled.img`
  width: calc(5 * var(--unit));
  height: calc(5 * var(--unit));
  object-fit: contain;
`;

const Dot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid var(--c-white);
  &.active {
    background-color: var(--c-onBase-success);
    border: 1px solid var(--c-onBase-success);
  }
`;

const TopLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
`;

type Props = {
  so5LeaderboardGroups: readonly LeaderboardGroupPicker_so5LeaderboardGroup[];
  selectedGroup?: string;
  onSelect: (slug: string) => void;
} & Pick<ComponentProps<typeof ShieldButton>, 'size'>;

export const LeaderboardGroupPicker = ({
  so5LeaderboardGroups,
  selectedGroup,
  onSelect,
  size = 'sm',
}: Props) => {
  const selectedGroupData = so5LeaderboardGroups.find(
    g => g.slug === selectedGroup
  );

  const fixture = so5LeaderboardGroups.at(0)?.so5Fixture;

  const fixtureCutOffDate = fixture?.cutOffDate;

  return (
    <Dropdown
      align="center"
      gap={8}
      label={props => (
        <ShieldButton
          type="button"
          size={size}
          icon={
            selectedGroupData?.iconUrl ? (
              <GroupIcon
                className={size === 'md' ? 'md' : undefined}
                src={selectedGroupData.iconUrl}
                alt=""
                role="presentation"
              />
            ) : undefined
          }
          orientation="down"
          {...props}
        />
      )}
    >
      {({ closeDropdown }) => (
        <>
          <PickerHeader onClose={closeDropdown}>
            <Vertical gap={0.5} center as="span">
              <FormattedMessage defaultMessage="Choose league" />
              {!!fixtureCutOffDate && !isPast(fixtureCutOffDate) && (
                <Horizontal gap={0.5} center>
                  <FontAwesomeIcon
                    icon={iconClock}
                    color="var(--c-yellow-400)"
                    size="xs"
                  />
                  <BrandXXS color="var(--c-yellow-400)">
                    <TimeLeft time={fixtureCutOffDate} />
                  </BrandXXS>
                </Horizontal>
              )}
              {!(fixtureCutOffDate && !isPast(fixtureCutOffDate)) &&
                !!fixture &&
                isFixtureLive(fixture) && (
                  <Horizontal gap={0.5} center>
                    <LiveIndicator />
                    <BrandXXS color="var(--c-red-400)">
                      <FormattedMessage defaultMessage="Live" />
                    </BrandXXS>
                  </Horizontal>
                )}
            </Vertical>
          </PickerHeader>
          <PickerOptionsGrid>
            {so5LeaderboardGroups.map(group => {
              const teamsCap = group.so5Leaderboards.at(0)?.teamsCap;

              return (
                <PickerOption
                  key={group.slug}
                  disabled={!group.hasLeaderboards}
                  className={classNames({
                    selected: selectedGroup === group.slug,
                  })}
                  onClick={() => {
                    const slug = group.so5Leaderboards.at(0)?.slug;
                    if (slug) {
                      onSelect(slug);
                      closeDropdown();
                    }
                  }}
                  style={
                    {
                      '--board-color': group.color,
                      overflow: 'hidden',
                    } as CSSProperties
                  }
                >
                  {!group.hasLeaderboards && <LockOverlay icon={iconLock} />}
                  <Vertical center gap={2}>
                    {!!group.iconUrl && (
                      <GridIcon
                        src={group.iconUrl}
                        alt={group.displayName ?? ''}
                      />
                    )}
                    {!isEqual(
                      group.cutOffDate,
                      group.so5Fixture?.cutOffDate
                    ) && (
                      <TopLeft>
                        <LeagueDelayedEntryChip
                          so5LeaderboardGroup={group}
                          showLabel={false}
                        />
                      </TopLeft>
                    )}
                    {!!group.hasLeaderboards &&
                      (teamsCap ? (
                        <Horizontal>
                          {range(teamsCap).map(i => (
                            <Dot
                              key={i}
                              className={classNames({
                                active: i < group.mySo5LineupsCount,
                              })}
                            />
                          ))}
                        </Horizontal>
                      ) : (
                        <BrandXS>{group.mySo5LineupsCount}</BrandXS>
                      ))}
                  </Vertical>
                </PickerOption>
              );
            })}
          </PickerOptionsGrid>
        </>
      )}
    </Dropdown>
  );
};

LeaderboardGroupPicker.fragments = {
  so5LeaderboardGroup: gql`
    fragment LeaderboardGroupPicker_so5LeaderboardGroup on So5LeaderboardGroup {
      slug
      iconUrl
      displayName
      mySo5LineupsCount
      hasLeaderboards
      color(variant: PRIMARY)
      cutOffDate
      so5Leaderboards {
        slug
        teamsCap
        cutOffDate
        mainRarityType
      }
      so5Fixture {
        slug
        cutOffDate
        ...isFixtureLive_so5Fixture
      }
      ...LeagueDelayedEntryChip_so5LeaderboardGroup
    }
    ${LeagueDelayedEntryChip.fragments.so5LeaderboardGroup}
    ${isFixtureLive.fragments.so5Fixture}
  ` as TypedDocumentNode<LeaderboardGroupPicker_so5LeaderboardGroup>,
};
